'use client'

import * as React from 'react'

type AppSidebarContextValue = {
  collapsed: boolean
  setCollapsed: (value: boolean) => void
  toggleCollapsed: () => void
  isCompanieSelectorCollapsed: boolean
  toggleCompanieSelectorCollapsed: () => void
}

const AppSidebarContext = React.createContext<AppSidebarContextValue | null>(null)

const STORAGE_KEY = 'app:sidebar:collapsed'

export type AppSidebarProviderProps = {
  children: React.ReactNode
  defaultCollapsed?: boolean
}

export function AppSidebarProvider({children, defaultCollapsed = false}: AppSidebarProviderProps) {
  const [collapsed, setCollapsedState] = React.useState(defaultCollapsed)
  const [isCompanieSelectorCollapsed, setIsCompanieSelectorCollapsed] = React.useState(false)

  React.useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY)
      if (raw == null) return
      setCollapsedState(raw === '1')
    } catch {}
  }, [])

  const setCollapsed = React.useCallback((value: boolean) => {
    setCollapsedState(value)
    try {
      window.localStorage.setItem(STORAGE_KEY, value ? '1' : '0')
    } catch {}
  }, [])

  const toggleCollapsed = React.useCallback(() => {
    setCollapsed(!collapsed)
  }, [collapsed, setCollapsed])

  const toggleCompanieSelectorCollapsed = React.useCallback(() => {
    setIsCompanieSelectorCollapsed(!isCompanieSelectorCollapsed)
  }, [isCompanieSelectorCollapsed, setIsCompanieSelectorCollapsed])

  const value = React.useMemo(
    () => ({
      collapsed,
      setCollapsed,
      toggleCollapsed,
      toggleCompanieSelectorCollapsed,
      isCompanieSelectorCollapsed,
    }),
    [collapsed, setCollapsed, toggleCollapsed, toggleCompanieSelectorCollapsed, isCompanieSelectorCollapsed],
  )

  return <AppSidebarContext.Provider value={value}>{children}</AppSidebarContext.Provider>
}

export function useAppSidebar() {
  const ctx = React.useContext(AppSidebarContext)
  if (!ctx) {
    throw new Error('useAppSidebar must be used within <AppSidebarProvider />')
  }
  return ctx
}
